import { ConfigProvider, Table, Tooltip, theme } from 'antd';
import type {
  ColumnsType,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import {
  CSSProperties,
  Key,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { sortOrderForColumn } from '@/components/tables/DataTable/dataTableEchoSortUtils';
import { SelectableMetricsContext } from './SelectableMetricsContext';
import { useResetOnFilterKeyChange } from './useResetOnFilterKeyChange';
import { useQueryUrl } from '@/services/queryUtils';
import { SortField } from '@/types';
import { FilterParams, useFilters } from '@/services/revenueApi';
import { SelectedItem } from '@/types/content';
import { formatNumber } from '@/utils';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SELECTED_COUNT = 5;

const EXPANDED_PARENT_ROW_CLASS = 'cp-expanded-parent';
const EXPANDED_CONTENT_ROW_CLASS = 'cp-expanded-content';
const DISABLED_ROW_CLASS = 'cp-disabled-row';

interface PaginationParams {
  page: number;
  pageSize: number;
  sortField: SortField;
}

export type PaginatedQueryParams<TAdditionalParams = undefined> = {
  filters: FilterParams;
  pagination?: PaginationParams;
} & (TAdditionalParams extends undefined
  ? { additionalParams?: never }
  : { additionalParams: TAdditionalParams });

type ServerSidePaginatedTableProps<TItem extends SelectedItem, TAdditionalParams = undefined> = {
  columns: ColumnsType<TItem>;
  expandedRowRender?: (item: TItem) => ReactNode;
  urlBuilder: (paginatedQueryParams: PaginatedQueryParams<TAdditionalParams>) => string;
  defaultSort: SortField;
  /** Gate the list query until URL filters have synced. */
  isSynced?: boolean;
  scrollX?: number;
  /** Rows this returns `true` for get a disabled checkbox, a greyed-out style, and are excluded from default chart-selection seeding. */
  isRowDisabled?: (item: TItem) => boolean;
  /** Tooltip shown when hovering the checkbox of a row disabled by {@link isRowDisabled}. */
  disabledRowTooltip?: string;
  /**
   * Additional params used only when computing the selection-reset key, instead of
   * `additionalParams`. Pass a value that stays constant across a dimension that affects the
   * fetch but should NOT reset selection/pagination (e.g. a fixed value for a param that
   * otherwise varies, like a toggle unrelated to the dataset identity).
   */
  resetKeyAdditionalParams?: TAdditionalParams;
} & (TAdditionalParams extends undefined
  ? { additionalParams?: never }
  : { additionalParams: TAdditionalParams });

const EXPAND_COLUMN_WIDTH = 48;

export function ServerSidePaginatedTable<
  TItem extends SelectedItem,
  TAdditionalParams = undefined,
>({
  columns,
  expandedRowRender,
  urlBuilder,
  defaultSort,
  additionalParams,
  isSynced = true,
  scrollX,
  isRowDisabled,
  disabledRowTooltip,
  resetKeyAdditionalParams,
}: ServerSidePaginatedTableProps<TItem, TAdditionalParams>) {
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState<SortField>(defaultSort);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
  const clearExpandedRows = useCallback(() => setExpandedRowKeys([]), []);

  const { selectedItems, select, seedSelection, reset, updateListStatus } =
    useContext(SelectableMetricsContext);

  const pagination = { page, pageSize, sortField };
  const filters = useFilters();
  // Filter-only dataset key (omit pagination so page/sort changes do not reset selection).
  // `resetKeyAdditionalParams` lets a caller exclude a dimension that affects the fetch but
  // should not reset selection (see its doc comment on the prop type).
  const filterKeyParams = resetKeyAdditionalParams ?? additionalParams;
  const filterKey = urlBuilder({
    filters,
    ...(filterKeyParams !== undefined ? { additionalParams: filterKeyParams } : {}),
  } as unknown as PaginatedQueryParams<TAdditionalParams>);
  useResetOnFilterKeyChange(filterKey, reset, setPage, clearExpandedRows);

  useEffect(() => {
    setSortField({ key: defaultSort.key, direction: defaultSort.direction });
    setPage(1);
  }, [defaultSort.key, defaultSort.direction]);

  /**
   * I got the following from Claude re: the `unknown` below. It's a bit annoying that typescript works this way, but I think I'm fine with it.
   *
   * > Root cause: PaginatedQueryParams<TAdditionalParams> is a conditional type keyed on the generic TAdditionalParams, which stays unresolved
   * > inside the function body. TypeScript won't distribute a conditional type over an unresolved generic, so it can't confirm the literal is a
   * > valid member of either branch — even a same-shape as cast gets rejected as "insufficient overlap" (TS2352). The fix is the double cast
   * > the error message suggests: as unknown as PaginatedQueryParams<TAdditionalParams> — going through unknown bypasses the overlap check
   * > since you're asserting "trust me" rather than "these are structurally compatible."
   */
  const queryParams = {
    filters,
    pagination,
    additionalParams,
  } as unknown as PaginatedQueryParams<TAdditionalParams>;
  const {
    data: { items, total } = {},
    isPlaceholderData,
    isPending,
  } = useQueryUrl<{ items: TItem[]; total: number }, PaginatedQueryParams<TAdditionalParams>>(
    urlBuilder,
    queryParams,
    { enabled: isSynced, placeholderData: keepPreviousData }
  );

  // Share list status with the chart. Layout effect (not render-phase setState) so the
  // first paint still uses the context default (pending) and pagination does not flip the chart.
  useLayoutEffect(() => {
    const isListPending = isPending && !isPlaceholderData;
    const seedableCount = (items ?? []).filter((item) => !isRowDisabled?.(item)).length;
    const isListEmpty = !isPending && !isPlaceholderData && seedableCount === 0;
    updateListStatus({ isListPending, isListEmpty });
  }, [isPending, isPlaceholderData, items, isRowDisabled, updateListStatus]);

  // Drop expansions for rows that are no longer on the current page so IDs cannot reopen
  // (and refetch platform breakdown) when they reappear later without a new expand click.
  useEffect(() => {
    if (isPlaceholderData || isPending) return;
    const visibleIds = new Set((items ?? []).map((item) => item.id));
    setExpandedRowKeys((previousKeys) => {
      const nextKeys = previousKeys.filter((key) => visibleIds.has(String(key)));
      return nextKeys.length === previousKeys.length ? previousKeys : nextKeys;
    });
  }, [items, isPlaceholderData, isPending]);

  // Seed the first N rows once page-1 data arrives (or no-op with [] when empty).
  // `page !== 1` skips the commit where filters reset but page hasn't flipped yet, so we don't
  // seed from the previous page's rows.
  useEffect(() => {
    if (page !== 1 || isPlaceholderData || isPending) return;
    seedSelection(
      (items ?? [])
        .filter((item) => !isRowDisabled?.(item))
        .slice(0, DEFAULT_SELECTED_COUNT)
        .map(({ id, label }) => ({ id, label }))
    );
  }, [page, items, isPlaceholderData, isPending, seedSelection, isRowDisabled]);

  const onRowSelectionChange = (_: Key[], rows: TItem[]) => {
    if (isPending || isPlaceholderData) return;
    select(rows.map(({ id, label }) => ({ id, label })));
  };

  const onTableChange = (
    paginationConfig: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TItem> | SorterResult<TItem>[]
  ) => {
    if (paginationConfig.current !== undefined) {
      setPage(paginationConfig.current);
    }

    const active = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortKey = active?.columnKey ?? active?.field;
    const nextSort: SortField =
      typeof sortKey === 'string' && active.order
        ? {
            key: sortKey,
            direction: active.order === 'descend' ? 'desc' : 'asc',
          }
        : defaultSort;

    if (nextSort.key === sortField.key && nextSort.direction === sortField.direction) return;
    setSortField(nextSort);
    setPage(1);
  };

  // Keep Ant header indicators in sync with query sortField (controlled sortOrder).
  // defaultSortOrder alone is uncontrolled and desyncs when Ant's cycle cancels the sorter.
  const columnsWithSortOrder = useMemo(
    () =>
      columns.map((column) => {
        if (!column.sorter) return column;

        const rawColumnKey = column.key ?? ('dataIndex' in column ? column.dataIndex : undefined);
        const columnKey =
          typeof rawColumnKey === 'string' || typeof rawColumnKey === 'number'
            ? rawColumnKey
            : undefined;

        return {
          ...column,
          sortOrder: sortOrderForColumn(columnKey, sortField),
        };
      }),
    [columns, sortField]
  );

  const expandedRowKeySet = useMemo(() => new Set(expandedRowKeys), [expandedRowKeys]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            rowHoverBg: 'transparent',
            rowSelectedHoverBg: 'transparent',
            rowSelectedBg: 'transparent',
            bodySortBg: 'transparent',
          },
        },
      }}
    >
      <div
        style={
          {
            '--cp-expanded-parent-bg': token.colorFillSecondary,
          } as CSSProperties
        }
        className={[
          '[&_tr.cp-expanded-parent>td]:!bg-[var(--cp-expanded-parent-bg)]',
          '[&_tr.cp-expanded-content>td]:!p-0',
          '[&_tr.cp-disabled-row>td]:opacity-45',
        ].join(' ')}
      >
        <Table
          columns={columnsWithSortOrder}
          dataSource={items?.map((item) => ({ key: item.id, ...item }))}
          loading={isPending || isPlaceholderData}
          scroll={{ x: scrollX ?? true }}
          tableLayout={scrollX != null ? 'fixed' : undefined}
          rowClassName={(record) =>
            [
              expandedRowKeySet.has(record.id) ? EXPANDED_PARENT_ROW_CLASS : '',
              isRowDisabled?.(record) ? DISABLED_ROW_CLASS : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          expandable={
            expandedRowRender
              ? {
                  expandedRowRender,
                  expandedRowKeys,
                  onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                  expandedRowClassName: EXPANDED_CONTENT_ROW_CLASS,
                  indentSize: 0,
                  columnWidth: EXPAND_COLUMN_WIDTH,
                }
              : undefined
          }
          showSorterTooltip={false}
          sortDirections={['descend', 'ascend']}
          rowSelection={{
            preserveSelectedRowKeys: true,
            onChange: onRowSelectionChange,
            selectedRowKeys: selectedItems.map((item) => item.id),
            getCheckboxProps: (record) => ({ disabled: isRowDisabled?.(record) ?? false }),
            // Colour the checkbox with the chart series colour already on selectedItems.
            renderCell: (_checked, record, _index, originNode) => {
              const color = selectedItems.find((item) => item.id === record.id)?.color;
              const cell = color ? (
                <ConfigProvider
                  theme={{ token: { colorPrimary: color, colorPrimaryHover: color } }}
                >
                  {originNode}
                </ConfigProvider>
              ) : (
                originNode
              );

              return isRowDisabled?.(record) && disabledRowTooltip ? (
                <Tooltip title={disabledRowTooltip}>{cell}</Tooltip>
              ) : (
                cell
              );
            },
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 15, 20],
            showTotal: (itemTotal, range) =>
              `${formatNumber('en', range[0])}-${formatNumber('en', range[1])} of ${formatNumber('en', itemTotal)} items`,
            placement: ['bottomStart'],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
          onChange={onTableChange}
        />
      </div>
    </ConfigProvider>
  );
}
